import React from 'react';
import { Loader2Icon, CheckCircle2Icon, XCircleIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card } from '../ui/card';

interface BuildNodesToolCallProps {
  toolName: string;
  toolCallId: string;
  args: any;
  argsText: string;
  result?: string;
  status?: { type: 'running' | 'complete' | 'incomplete'; reason?: string };
}

export const BuildNodesToolCall: React.FC<BuildNodesToolCallProps> = ({
  args,
  argsText,
  result,
  status,
}) => {
  const isRunning = status?.type === 'running' || (status === undefined && result === undefined);
  const isError = status?.type === 'incomplete' && status.reason === 'error';
  const isComplete = !isRunning && !isError && result !== undefined;

  // Parse selector from args
  let selector = '';
  try {
    const parsedArgs = typeof args === 'object' ? args : JSON.parse(argsText);
    selector = parsedArgs?.selector || parsedArgs?.node_selector || '';
  } catch (e) {
    // Ignore parse errors
  }

  // Check if the result indicates success
  let isSuccess = false;
  let builtCount = 0;
  if (isComplete && result) {
    try {
      const parsed = typeof result === 'string' ? JSON.parse(result) : result;
      isSuccess = parsed?.success === true;
      builtCount = parsed?.built_count || parsed?.count || 0;
    } catch (e) {
      isSuccess = !result.toLowerCase().includes('error');
    }
  }

  const hasError = isError || (isComplete && !isSuccess);

  return (
    <Card>
      <div className={cn(
        "p-3",
        isRunning
          ? "bg-primary/10 border-b border-primary/20"
          : "bg-muted/50 border-b border-border"
      )}>
        <div className="flex items-center gap-3">
          <div className="flex-1 space-y-1">
            {isRunning ? (
              <div className="flex items-center gap-2">
                <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
                <p className="text-sm m-0 text-foreground">
                  Building nodes{selector ? `: ${selector}` : ''}
                </p>
              </div>
            ) : hasError ? (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 flex-shrink-0" />
                  <p className="text-sm m-0 text-muted-foreground">
                    Building nodes{selector ? `: ${selector}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
                  <p className="text-sm m-0 text-destructive">
                    Build failed
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 flex-shrink-0" />
                  <p className="text-sm m-0 text-muted-foreground">
                    Building nodes{selector ? `: ${selector}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2Icon className="h-4 w-4 text-success flex-shrink-0" />
                  <p className="text-sm m-0 text-foreground">
                    Build completed successfully{builtCount > 0 ? ` (${builtCount} nodes)` : ''}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};
